import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  ACCESS_MGMT_ENVIRONMENT,
  AccessMgmtModule,
  ACCESS_MGMT_APPLICATIONID,
  ACCESS_MGMT_CONFIG_FILE,
  AccessMgmtClientOptions,
  ACCESS_MGMT_CLIENT_OPTIONS,
} from '@stantec/access-mgmt';
import { FormlyModule } from '@ngx-formly/core';
import { TableUnsortModule } from '@stantec/basin/core';
import {
  BasinFormlyModule,
  BasinFormlyConfig,
} from '@stantec/basin/basin-formly';
import { MainModule } from '@stantec/basin/main';
import { ActionBarModule } from '@stantec/basin/action-bar';
import { MenuModule } from 'primeng/menu';
import { LottieAnimationPlayerModule } from '@stantec/basin/lottie-animation-player';
import { LottieModule, LottieCacheModule } from 'ngx-lottie';
import player from 'lottie-web';
import { PrimaryNavigationComponent } from './global/components/primary-navigation/primary-navigation.component';
import { LoadingIndicatorComponent } from './global/components/loading-indicator/loading-indicator.component';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { PredictorModule } from './predictor/predictor.module';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { ApplicationState } from './state/application.state';
import { UserState } from './state/user/user.state';
import { NotificationState } from './state/notifications/notifications.state';
import { FeaturesState } from './state/features/features.state';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CircledIconModule } from '@stantec/basin/circled-icon';
import { PredictorModelState } from './state/predictor-model/predictor-model.state';
import { ScenarioSelectionModalComponent } from './global/components/scenario-selection-modal/scenario-selection-modal.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { APP_CONFIG, AppConfig } from './appconfig/appconfig';
import { FocusNextWrapperComponent } from './predictor/components/focus-next-wrapper/focus-next-wrapper.component';
import { ManagerState } from './state/manager/manager.state';
import { TableModule } from 'primeng/table';
import { SearchboxModule } from '@stantec/basin/searchbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InactivityInterceptor } from './predictor/interceptors/inactivity.interceptor';
import { SimulationTrackerState } from './state/simulation-tracker/simulation-tracker.state';
import { DropdownModule } from 'primeng/dropdown';
import { ListItemModule } from '@stantec/basin/list-item';
import { BooleanIconModule } from '@stantec/basin/boolean-icon';
import { NGX_CLARITY_CONFIG_TOKEN, provideClarity } from '@stantec/ngx-clarity';
import { PaginatorModule } from 'primeng/paginator';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { StudyAreaState } from './state/study-area/study-area.state';
import { StudyAreaPageComponent } from './predictor/pages/study-area-page/study-area-page.component';
import { StepperModule } from '@stantec/basin/stepper';
import { StepsModule } from 'primeng/steps';
import { PrimeTourStepComponent } from './predictor/pages/study-area-page/prime-tour-step.component';
import { PrimeTourAnchorDirective } from './predictor/services/tour-anchor.directive';
import { DialogModule } from 'primeng/dialog';
import { TextboxWithUnitsModule } from '@stantec/basin/textbox-with-units';
import { TabViewModule } from 'primeng/tabview';
import { EnterpriseLicenseState } from './state/enterprise-license/enterprise-license.state';
import { CreateStudyAreaModule } from './predictor-enterprise-license/create-study-area/create-study-area.module';
import { AvailableStudyAreaComponent } from './predictor-enterprise-license/shared/components/available-study-area/available-study-area.component';
import { CreateNewStudyAreaComponent } from './predictor-enterprise-license/create-study-area/pages/create-new-study-area/create-new-study-area.component';
import { ReviewStudyAreaComponent } from './predictor-enterprise-license/create-study-area/pages/review-study-area/review-study-area.component';
import { StudyAreaWorkflowComponent } from './predictor-enterprise-license/create-study-area/pages/study-area-workflow/study-area-workflow.component';
import { LicenseStudyAreaState } from './state/License-Study-Area/License-Study-Area.state';
import { AccordionModule } from 'primeng/accordion';
import { SearchPipe } from './predictor-enterprise-license/shared/pipes/search';
import { ExpiryNotificationComponent } from './global/components/expiry-notification/expiry-notification.component';

declare const window: Window & { directLinkOrganizationId?: string };

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}

export function accessMgmtEnvironment(configService: AppConfig) {
  return configService.digitalAccessPortal?.environment;
}

export function accessMgmtApplicationId(configService: AppConfig) {
  return configService.digitalAccessPortal?.applicationId;
}

export function getClarityConfig(configService: AppConfig) {
  return {
    enabled: !configService.clarityProjectId ? false : true,
    projectId: configService.clarityProjectId ?? '',
  };
}

@NgModule({
  declarations: [
    AppComponent,
    PrimaryNavigationComponent,
    LoadingIndicatorComponent,
    ScenarioSelectionModalComponent,    
    StudyAreaPageComponent,
    PrimeTourStepComponent,
    PrimeTourAnchorDirective,
    CreateNewStudyAreaComponent,
    ReviewStudyAreaComponent,
    StudyAreaWorkflowComponent,
    AvailableStudyAreaComponent,
    SearchPipe,
    ExpiryNotificationComponent,
  ],
  imports: [
    BrowserModule,
    TextboxWithUnitsModule,
    BrowserAnimationsModule,
    CardModule,
    TabViewModule,
    LottieAnimationPlayerModule,
    LottieModule.forRoot({ player: playerFactory }),
    LottieCacheModule.forRoot(),
    AppRoutingModule,
    AccessMgmtModule,
    HttpClientModule,
    PaginatorModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    BasinFormlyModule,
    DropdownModule,
    DialogModule,
    ListItemModule,
    TableModule,
    TableUnsortModule,
    ButtonModule,
    FormsModule,
    ConfirmDialogModule,
    BooleanIconModule,
    StepperModule,
    StepsModule,
    FormlyModule.forRoot({
      ...BasinFormlyConfig,
      wrappers: [
        ...BasinFormlyConfig.wrappers,
        { name: 'focus-next-wrapper', component: FocusNextWrapperComponent },
      ],
    }),
    FormlyPrimeNGModule,
    MainModule,
    ActionBarModule,
    MenuModule,
    PredictorModule,
    CircledIconModule,
    SearchboxModule,
    ToastModule,
    InputSwitchModule,
    CreateStudyAreaModule,
    AccordionModule,
    NgxsModule.forRoot(
      [
        ApplicationState,
        UserState,
        NotificationState,
        PredictorModelState,
        ManagerState,
        FeaturesState,
        SimulationTrackerState,
        StudyAreaState,
        EnterpriseLicenseState,
        LicenseStudyAreaState,
      ],
      {
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      }
    ),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InactivityInterceptor,
      multi: true,
    },
    {
      provide: ACCESS_MGMT_ENVIRONMENT,
      useFactory: accessMgmtEnvironment,
      deps: [APP_CONFIG],
    },
    {
      provide: ACCESS_MGMT_APPLICATIONID,
      useFactory: accessMgmtApplicationId,
      deps: [APP_CONFIG],
    },
    {
      provide: ACCESS_MGMT_CLIENT_OPTIONS,
      useFactory: getAccessMgmtOptions,
      deps: [APP_CONFIG],
    },
    {
      provide: ACCESS_MGMT_CONFIG_FILE,
      useValue: 'api/clientconfig',
    },
    provideClarity({ enabled: false, projectId: '' }),
    {
      provide: NGX_CLARITY_CONFIG_TOKEN,
      useFactory: getClarityConfig,
      deps: [APP_CONFIG],
    },
    MessageService,
  ],
  exports: [PrimeTourAnchorDirective, SearchPipe],
  bootstrap: [AppComponent],
})
export class AppModule { }

export function getAccessMgmtOptions(configService: AppConfig) {
  console.debug('getAccessMgmtOptions:', configService);
  return {
    applicationId: configService.digitalAccessPortal?.applicationId,
    organizationId:
      configService.digitalAccessPortal?.organizationId ??
      window.directLinkOrganizationId,
    redirectUri: new URL(
      window.location.pathname.split('/')[1],
      new URL('/', window.location.href)
    ).toString(),
    redirectRoute: '/',
    msalResourceMap: new Map([
      ['/api', ['https://stantecb2c.onmicrosoft.com/azure-sia-apps/Access']],
    ]),
    ssoPolicyEnabled: true
  } as AccessMgmtClientOptions;
}
